import { ROUTES } from "@/app/constants/routes_sidebar/routes.constant";

type RouteImporter = () => Promise<object>;

type PrefetchEntry = {
  key: string;
  importModule: RouteImporter;
};

type BusinessPrefetchRule = {
  matches: (pathname: string) => boolean;
  entry: PrefetchEntry;
};

const IDLE_TIMEOUT_MS = 3000;

const BUSINESS_PREFETCH_RULES: readonly BusinessPrefetchRule[] = [
  {
    matches: (pathname: string): boolean => pathname === ROUTES.ROOT,
    entry: {
      key: "roster",
      importModule: (): Promise<object> => import("@/features/roster/pages"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(ROUTES.PROJECT_REGISTER),
    entry: {
      key: "project-register",
      importModule: (): Promise<object> =>
        import("@/features/projectRegister/pages/register"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(ROUTES.ESTIMATES),
    entry: {
      key: "estimates",
      importModule: (): Promise<object> => import("@/features/estimates"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(ROUTES.PROJECTS),
    entry: {
      key: "projects",
      importModule: (): Promise<object> => import("@/features/projects"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(ROUTES.TASK_CLAIMS),
    entry: {
      key: "task-and-claims",
      importModule: (): Promise<object> =>
        import("@/features/taskAndClaims/pages"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(ROUTES.WORKFORCE),
    entry: {
      key: "workforce",
      importModule: (): Promise<object> => import("@/features/workforce"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(ROUTES.CUSTOMERS),
    entry: {
      key: "clients-customer",
      importModule: (): Promise<object> =>
        import("@/features/clients/pages/customer"),
    },
  },
  {
    matches: (pathname: string): boolean => pathname.endsWith(ROUTES.PROSPECT),
    entry: {
      key: "clients-prospect",
      importModule: (): Promise<object> =>
        import("@/features/clients/pages/prospect"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(`${ROUTES.INVOICING}${ROUTES.CONTRACT_INVOICE}`),
    entry: {
      key: "invoicing-contract",
      importModule: (): Promise<object> =>
        import("@/features/invoicing/pages/contractInvoice"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(`${ROUTES.INVOICING}${ROUTES.DOCKET_INVOICE}`),
    entry: {
      key: "invoicing-docket",
      importModule: (): Promise<object> =>
        import("@/features/invoicing/pages/docketInvoice"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(`${ROUTES.INVOICING}${ROUTES.XERO_INVOICE}`),
    entry: {
      key: "invoicing-xero",
      importModule: (): Promise<object> =>
        import("@/features/invoicing/pages/xeroInvoice"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(`${ROUTES.REPORTS}${ROUTES.PROJECT_DAILY_REPORTS}`),
    entry: {
      key: "reports-project-daily",
      importModule: (): Promise<object> =>
        import("@/features/projectDailyReport"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(`${ROUTES.REPORTS}${ROUTES.TIPPING_REPORT}`),
    entry: {
      key: "reports-tipping",
      importModule: (): Promise<object> =>
        import("@/features/tippingReport/pages"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(`${ROUTES.REPORTS}${ROUTES.WORK_DOCKET}`),
    entry: {
      key: "reports-docket",
      importModule: (): Promise<object> =>
        import("@/features/docket/context/WorkDocketWrapper"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(`${ROUTES.REPORTS}${ROUTES.DAILY_REPORTS}`),
    entry: {
      key: "reports-daily",
      importModule: (): Promise<object> =>
        import("@/features/dailyReport/pages/index"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(`${ROUTES.REPORTS}${ROUTES.REPORT_QUESTIONS}`),
    entry: {
      key: "reports-questions",
      importModule: (): Promise<object> =>
        import("@/features/reportQuestions/pages"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(`${ROUTES.TIME_TRACKING}${ROUTES.SUMMARY}`),
    entry: {
      key: "time-tracking-summary",
      importModule: (): Promise<object> =>
        import("@/features/timeTrackingPayroll/pages/timesheetSummary/index"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(`${ROUTES.TIME_TRACKING}${ROUTES.TIME_SHEET}`),
    entry: {
      key: "time-tracking-sheet",
      importModule: (): Promise<object> =>
        import("@/features/timeTrackingPayroll/pages/timeSheet/index"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(`${ROUTES.TIME_TRACKING}${ROUTES.SUB_CONTRACTOR}`),
    entry: {
      key: "time-tracking-sub-contractor",
      importModule: (): Promise<object> =>
        import("@/features/timeTrackingPayroll/pages/subContractor/index"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(`${ROUTES.TIME_TRACKING}${ROUTES.SETTINGS}`),
    entry: {
      key: "time-tracking-settings",
      importModule: (): Promise<object> =>
        import("@/features/timeTrackingPayroll/pages/settings/index"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(`${ROUTES.INTELLIFLOW}${ROUTES.DOCUMENT_PARSER}`),
    entry: {
      key: "intelliflow",
      importModule: (): Promise<object> => import("@/features/intelliflow"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(ROUTES.MESSAGES),
    entry: {
      key: "messages",
      importModule: (): Promise<object> => import("@/features/messages"),
    },
  },
  {
    matches: (pathname: string): boolean => pathname.startsWith(ROUTES.ITEMS),
    entry: {
      key: "items",
      importModule: (): Promise<object> =>
        import("@/features/items/pages/ItemsPage"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(`${ROUTES.OHS}${ROUTES.INDUCTION}`),
    entry: {
      key: "ohs-induction",
      importModule: (): Promise<object> => import("@/features/induction"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(ROUTES.SETTINGS),
    entry: {
      key: "settings",
      importModule: (): Promise<object> =>
        import("@/features/settings/pages/WorkspacePortalSettingsPage"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(ROUTES.ACTIVITY),
    entry: {
      key: "activity",
      importModule: (): Promise<object> => import("@/features/activity"),
    },
  },
  {
    matches: (pathname: string): boolean =>
      pathname.startsWith(ROUTES.MAINTENANCE),
    entry: {
      key: "maintenance",
      importModule: (): Promise<object> =>
        import("@/features/maintenance/pages/MaintenancePage"),
    },
  },
];

export { BUSINESS_PREFETCH_RULES, IDLE_TIMEOUT_MS };
export type { BusinessPrefetchRule, PrefetchEntry, RouteImporter };
